import React from 'react';
import { TrendingUp, DollarSign, PiggyBank, Briefcase, Target } from 'lucide-react';

// Predefined allocation templates
export const ALLOCATION_TEMPLATES = {
  current: {
    name: "Lifestyle Maximizer",
    description: "Maximum lifestyle allocation (55%) for living well now",
    icon: Target,
    color: "blue",
    allocation: { taxes: 20, business: 20, personal: 55, savings: 5 },
    bestFor: "High earners who prioritize current quality of life. Covers tax (20%) and business (20%) essentials while directing 55% to personal lifestyle. Best for agents with other retirement plans or who believe in enjoying success in the present."
  },
  fiftyThirtyTwenty: {
    name: "Even Split Method",
    description: "Balanced 20/20/35/25 distribution across all priorities",
    icon: TrendingUp,
    color: "green",
    allocation: { taxes: 20, business: 20, personal: 35, savings: 25 },
    bestFor: "Realistic business expenses (20%) with strong savings focus (25%). Balanced approach between comfortable living (35%) and aggressive wealth building."
  },
  profitFirst: {
    name: "Profit First",
    description: "Take profit first, keep operations lean",
    icon: Briefcase,
    color: "purple",
    allocation: { taxes: 15, business: 25, personal: 50, savings: 10 },
    bestFor: "Forces business efficiency by limiting operating expenses to 25%. Prioritizes profit (10%) and strong owner's compensation (50%) while maintaining lean tax reserves."
  },
  aggressiveSaver: {
    name: "Aggressive Saver",
    description: "Maximize long-term wealth with 30% savings rate",
    icon: PiggyBank,
    color: "amber",
    allocation: { taxes: 20, business: 20, personal: 30, savings: 30 },
    bestFor: "Accelerated wealth building. 6x more savings than standard (5%). Keeps business lean (20%) and personal spending modest (30%) to prioritize future financial freedom."
  },
  balanced: {
    name: "Balanced Growth",
    description: "Harmonious 25/25/40/10 split for sustainable success",
    icon: DollarSign,
    color: "indigo",
    allocation: { taxes: 25, business: 25, personal: 40, savings: 10 },
    bestFor: "Perfect middle ground: higher tax cushion (25%) for peace of mind, equal business investment (25%), comfortable personal lifestyle (40%), and meaningful savings (10%). No extreme sacrifices. Ideal for agents seeking sustainable, stress-free growth."
  }
};

function AllocationTemplates({ selectedTemplate, onSelectTemplate, customAllocation, onCustomChange }) {
  const getColorClasses = (color, isSelected) => {
    const colors = {
      blue: isSelected ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-800 border-blue-200',
      green: isSelected ? 'bg-green-600 text-white' : 'bg-green-50 text-green-800 border-green-200',
      purple: isSelected ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-800 border-purple-200',
      amber: isSelected ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-800 border-amber-200',
      indigo: isSelected ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-800 border-indigo-200',
    };
    return colors[color] || colors.blue;
  };

  const handleCustomAllocationChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    onCustomChange({ ...customAllocation, [field]: numValue });
    // Any manual percentage edit forces the strategy back to "custom" so the
    // selection persists on reload (template_key=custom) and the user is never
    // silently snapped back to a preset that happens to share the same numbers.
    if (typeof onSelectTemplate === 'function' && selectedTemplate !== 'custom') {
      onSelectTemplate('custom');
    }
  };

  const calculateTotal = () => {
    const baseTotal = customAllocation.taxes + customAllocation.business + customAllocation.personal + customAllocation.savings;
    const customTotal = (customAllocation.custom_allocations || []).reduce((sum, alloc) => sum + (alloc.percentage || 0), 0);
    return baseTotal + customTotal;
  };

  const total = calculateTotal();
  // Overage tolerance mirrors FinancialPlanSetup (0.005 rejects 100.01%).
  const isValid = Math.abs(total - 100) < 0.005;
  const isOver = total > 100 + 0.005;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 dark:bg-blue-900 border-l-4 border-blue-500 dark:border-blue-400 p-4 rounded">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">💡 Choose Your Allocation Strategy</h3>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
          Choose how your system runs.
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
          Your allocation determines your lifestyle, savings, and growth speed.
        </p>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          Select a proven strategy or customize your own. This determines how your income is automatically divided across your system.
        </p>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(ALLOCATION_TEMPLATES).map(([key, template]) => {
          const Icon = template.icon;
          const isSelected = selectedTemplate === key;
          
          return (
            <button
              key={key}
              onClick={() => onSelectTemplate(key)}
              className={`text-left p-4 rounded-lg border-2 transition-all hover:shadow-md ${
                getColorClasses(template.color, isSelected)
              }`}
            >
              <div className="flex items-start space-x-3 mb-3">
                <Icon className="w-6 h-6 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-bold text-base">{template.name}</h4>
                    {key === 'fiftyThirtyTwenty' && (
                      <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${isSelected ? 'bg-white/20 text-white' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>
                        🟢 Recommended for most users
                      </span>
                    )}
                  </div>
                  <p className={`text-sm ${isSelected ? 'opacity-90' : 'opacity-70'}`}>
                    {template.description}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                <div>Total Taxes After Business Exp.: <strong>{template.allocation.taxes}%</strong></div>
                <div>Business: <strong>{template.allocation.business}%</strong></div>
                <div>Personal: <strong>{template.allocation.personal}%</strong></div>
                <div>Savings: <strong>{template.allocation.savings}%</strong></div>
              </div>
              
              <p className={`text-xs italic ${isSelected ? 'opacity-90' : 'opacity-70'}`}>
                {template.bestFor}
              </p>
              
              {template.note && (
                <p className={`text-xs mt-2 ${isSelected ? 'opacity-90' : 'opacity-70'}`}>
                  Note: {template.note}
                </p>
              )}
            </button>
          );
        })}

        {/* Custom Template */}
        <button
          onClick={() => onSelectTemplate('custom')}
          className={`text-left p-4 rounded-lg border-2 transition-all hover:shadow-md ${
            selectedTemplate === 'custom' ? 'bg-gray-900 text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600'
          }`}
        >
          <div className="flex items-start space-x-3 mb-3">
            <Target className="w-6 h-6 flex-shrink-0" />
            <div>
              <h4 className="font-bold text-base">Custom</h4>
              <p className={`text-sm ${selectedTemplate === 'custom' ? 'opacity-90' : 'opacity-70'}`}>
                Set your own percentages
              </p>
            </div>
          </div>
          <p className={`text-xs italic ${selectedTemplate === 'custom' ? 'opacity-90' : 'opacity-70'}`}>
            Perfect for unique situations
          </p>
        </button>
      </div>

      {/* Custom Allocation Editor */}
      {selectedTemplate === 'custom' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-gray-300 dark:border-gray-600 p-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">Customize Your Allocation</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Total Taxes After Business Exp. (%)
              </label>
              <input
                type="number"
                value={customAllocation.taxes}
                onChange={(e) => handleCustomAllocationChange('taxes', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="0"
                max="100"
                step="0.1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Business (%)
              </label>
              <input
                type="number"
                value={customAllocation.business}
                onChange={(e) => handleCustomAllocationChange('business', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="0"
                max="100"
                step="0.1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Personal (%)
              </label>
              <input
                type="number"
                value={customAllocation.personal}
                onChange={(e) => handleCustomAllocationChange('personal', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="0"
                max="100"
                step="0.1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Savings (%)
              </label>
              <input
                type="number"
                value={customAllocation.savings}
                onChange={(e) => handleCustomAllocationChange('savings', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-blue-500"
                min="0"
                max="100"
                step="0.1"
              />
            </div>
          </div>

          {/* Total Validation */}
          <div className={`p-4 rounded-lg ${isValid ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700'}`} data-testid="allocation-total-validation">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900 dark:text-gray-100">Total:</span>
              <span className={`text-2xl font-bold ${isValid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {total.toFixed(1)}%
              </span>
            </div>
            {!isValid && isOver && (
              <p className="text-sm text-red-800 dark:text-red-300 mt-2">
                ⚠️ Allocations exceed 100%. Reduce one or more buckets before saving.
              </p>
            )}
            {!isValid && !isOver && (
              <p className="text-sm text-red-800 dark:text-red-300 mt-2">
                ⚠️ Percentages must add up to exactly 100%. Currently: {total.toFixed(1)}%
              </p>
            )}
            {isValid && (
              <p className="text-sm text-green-800 dark:text-green-300 mt-2">
                ✅ Perfect! Your allocation adds up to 100%
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AllocationTemplates;
